import { useState, useEffect, useMemo } from "react";
import { Calendar, dateFnsLocalizer, Views, View } from "react-big-calendar";
import { format, parse, startOfWeek, getDay } from "date-fns";
import { enUS } from "date-fns/locale";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Calendar as CalendarIcon } from "lucide-react";
import "react-big-calendar/lib/css/react-big-calendar.css";

const locales = {
  "en-US": enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

interface Placement {
  id: string;
  property_id: string;
  client_id: string;
  start_date: string;
  end_date: string | null;
  status: string;
  properties: {
    name: string;
  };
}

interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  resource: {
    status: string;
    propertyName: string;
  };
}

export const PlacementCalendar = () => {
  const [placements, setPlacements] = useState<Placement[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<View>(Views.MONTH);
  const [date, setDate] = useState(new Date());

  useEffect(() => {
    fetchPlacements();
  }, []);

  const fetchPlacements = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("placements")
        .select(`
          *,
          properties(name)
        `)
        .order("start_date", { ascending: true });

      if (error) throw error;
      setPlacements(data || []);
    } catch (error: any) {
      console.error("Error fetching placements:", error);
      toast.error("Failed to load placements");
    } finally {
      setLoading(false);
    }
  };

  const events: CalendarEvent[] = useMemo(() => {
    return placements.map((placement) => ({
      id: placement.id,
      title: placement.properties?.name || "Unknown Property",
      start: new Date(placement.start_date),
      end: placement.end_date ? new Date(placement.end_date) : new Date(new Date().setFullYear(new Date().getFullYear() + 1)),
      resource: {
        status: placement.status,
        propertyName: placement.properties?.name || "Unknown Property",
      },
    }));
  }, [placements]);

  const eventStyleGetter = (event: CalendarEvent) => {
    const statusColors: Record<string, string> = {
      pending: "bg-orange-500",
      active: "bg-green-500",
      completed: "bg-gray-500",
    };

    const bgColor = statusColors[event.resource.status] || "bg-blue-500";

    return {
      className: `${bgColor} text-white border-none rounded px-2 py-1`,
    };
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6 flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <CalendarIcon className="h-5 w-5" />
              Placement Calendar
            </CardTitle>
            <CardDescription>
              Track placement dates and property availability
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Badge variant="outline" className="bg-orange-500/10 text-orange-700 border-orange-500/20">
              Pending
            </Badge>
            <Badge variant="outline" className="bg-green-500/10 text-green-700 border-green-500/20">
              Active
            </Badge>
            <Badge variant="outline" className="bg-gray-500/10 text-gray-700 border-gray-500/20">
              Completed
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div style={{ height: "600px" }}>
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            view={view}
            onView={setView}
            date={date}
            onNavigate={setDate}
            eventPropGetter={eventStyleGetter}
            tooltipAccessor={(event) => 
              `${event.resource.propertyName} - ${event.resource.status}`
            }
            style={{ height: "100%" }}
          />
        </div>
      </CardContent>
    </Card>
  );
};
